import { useEffect, useState } from 'react';
import { keepPreviousData, useQuery } from '@tanstack/react-query';

import {
  PARTS_CATALOG_AUTOCOMPLETE_DEBOUNCE_MS,
  PARTS_CATALOG_AUTOCOMPLETE_LIMIT,
  PARTS_CATALOG_AUTOCOMPLETE_MIN_CHARS,
  PARTS_CATALOG_AUTOCOMPLETE_QUERY_KEY,
  PARTS_CATALOG_AUTOCOMPLETE_STALE_TIME_MS,
} from '@/constant/quoteDocuments';
import { partsCatalogApi } from '@/services/quoteDocuments';

export interface UsePartsCatalogAutocompleteOptions {
  q: string;
  enabled?: boolean;
}

export const usePartsCatalogAutocomplete = ({
  q,
  enabled = true,
}: UsePartsCatalogAutocompleteOptions) => {
  const [debouncedQuery, setDebouncedQuery] = useState('');

  useEffect(() => {
    const timeoutId = window.setTimeout(() => {
      setDebouncedQuery(q.trim());
    }, PARTS_CATALOG_AUTOCOMPLETE_DEBOUNCE_MS);
    return () => window.clearTimeout(timeoutId);
  }, [q]);

  const liveQuery = q.trim();
  const isQueryReady =
    debouncedQuery.length >= PARTS_CATALOG_AUTOCOMPLETE_MIN_CHARS;
  const matchesLiveQuery = isQueryReady && debouncedQuery === liveQuery;

  const query = useQuery({
    queryKey: [PARTS_CATALOG_AUTOCOMPLETE_QUERY_KEY, debouncedQuery],
    queryFn: ({ signal }) =>
      partsCatalogApi.autocomplete(
        debouncedQuery,
        PARTS_CATALOG_AUTOCOMPLETE_LIMIT,
        signal
      ),
    enabled: enabled && isQueryReady,
    staleTime: PARTS_CATALOG_AUTOCOMPLETE_STALE_TIME_MS,
    placeholderData: keepPreviousData,
  });

  return {
    hits:
      liveQuery.length >= PARTS_CATALOG_AUTOCOMPLETE_MIN_CHARS
        ? (query.data ?? [])
        : [],
    debouncedQuery,
    isQueryReady,
    matchesLiveQuery,
    isLoading: query.isLoading,
    isFetching: query.isFetching,
    isError: query.isError,
    error: query.error instanceof Error ? query.error.message : null,
    refetch: query.refetch,
  };
};
